import { Injectable, NgZone, signal, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { AppConfig } from '@env/app-config';
import { PagedResponse } from '@models/common/paged-response.model';
import { ApiResponse } from '@models/common/api-response.model';
import { NotificationDTO } from '@models/notification.model';

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private baseUrl = AppConfig.apiUrl;
  private apiUrl = `${this.baseUrl}/api/notifications`;
  private http = inject(HttpClient);
  // EventSource no está parcheado por zone.js: sus callbacks corren fuera de la zona de
  // Angular, así que las actualizaciones de signals se hacen dentro de zone.run() para que
  // la detección de cambios re-renderice la campana sin necesidad de refrescar la página.
  private zone = inject(NgZone);

  // Signal para el conteo de notificaciones no leídas
  unreadCount = signal<number>(0);

  // Signal para las últimas notificaciones
  recentNotifications = signal<NotificationDTO[]>([]);

  // Última notificación recibida en tiempo real (SSE). Permite a cualquier vista (p. ej. el
  // módulo de Notificaciones) reaccionar e incorporarla sin recargar. Null hasta el primer push.
  lastRealtime = signal<NotificationDTO | null>(null);

  // Actividad de ticket de soporte (evento SSE TRANSITORIO, no persistido): avisa que un ticket
  // recibió un mensaje para que quien lo tenga abierto refresque el hilo al instante, sin crear
  // notificación de campana. Objeto nuevo por push (seq) para re-disparar aunque sea el mismo id.
  lastTicketActivity = signal<{ ticketId: number; seq: number } | null>(null);
  private ticketActivitySeq = 0;

  /**
   * Obtener todas las notificaciones con paginación y filtros
   */
  getAllNotifications(
    page = 0,
    size = 10,
    search = '',
    status = 'all',
    userId?: number,
    companyId?: number
  ): Observable<PagedResponse<NotificationDTO>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('status', status);

    if (search) {
      params = params.set('search', search);
    }
    if (userId !== undefined) {
      params = params.set('userId', userId.toString());
    }
    if (companyId !== undefined) {
      params = params.set('companyId', companyId.toString());
    }

    return this.http.get<PagedResponse<NotificationDTO>>(this.apiUrl, { params });
  }

  /**
   * Obtener notificación por ID
   */
  getNotificationById(id: number): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(`${this.apiUrl}/${id}`);
  }

  /**
   * Obtener conteo de notificaciones no leídas
   */
  getUnreadCount(): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(`${this.apiUrl}/unread-count`).pipe(
      tap(response => {
        if (response.success && response.data) {
          this.unreadCount.set(response.data['unreadCount'] || 0);
        }
      })
    );
  }

  /**
   * Crear una nueva notificación (solo admin/superadmin)
   */
  createNotification(notification: Partial<NotificationDTO>): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(this.apiUrl, notification);
  }

  /**
   * Marcar notificación como leída
   */
  markAsRead(id: number): Observable<ApiResponse> {
    return this.http.put<ApiResponse>(`${this.apiUrl}/${id}/read`, {}).pipe(
      tap(() => this.refreshUnreadCount())
    );
  }

  /**
   * Marcar notificación como no leída
   */
  markAsUnread(id: number): Observable<ApiResponse> {
    return this.http.put<ApiResponse>(`${this.apiUrl}/${id}/unread`, {}).pipe(
      tap(() => this.refreshUnreadCount())
    );
  }

  /**
   * Marcar / desmarcar una notificación como importante (fijada por el usuario)
   */
  setImportant(id: number, important: boolean): Observable<ApiResponse> {
    const params = new HttpParams().set('important', important.toString());
    return this.http.put<ApiResponse>(`${this.apiUrl}/${id}/important`, {}, { params });
  }

  /**
   * Marcar todas las notificaciones como leídas
   */
  markAllAsRead(userId?: number): Observable<ApiResponse> {
    let params = new HttpParams();
    if (userId !== undefined) {
      params = params.set('userId', userId.toString());
    }
    return this.http.put<ApiResponse>(`${this.apiUrl}/read-all`, {}, { params }).pipe(
      tap(() => this.refreshUnreadCount())
    );
  }

  /**
   * Marcar múltiples notificaciones como leídas
   */
  markMultipleAsRead(notificationIds: number[]): Observable<ApiResponse> {
    return this.http.put<ApiResponse>(`${this.apiUrl}/read-multiple`, notificationIds).pipe(
      tap(() => this.refreshUnreadCount())
    );
  }

  /**
   * Eliminar una notificación
   */
  deleteNotification(id: number): Observable<ApiResponse> {
    return this.http.delete<ApiResponse>(`${this.apiUrl}/${id}`).pipe(
      tap(() => this.refreshUnreadCount())
    );
  }

  /**
   * Obtener notificaciones recientes (últimas 5).
   * Incluye leídas y no leídas ('all'): al marcar una como leída NO desaparece del panel,
   * solo queda atenuada. El contador de no leídas se gestiona aparte (unreadCount).
   */
  getRecentNotifications(): Observable<PagedResponse<NotificationDTO>> {
    return this.getAllNotifications(0, 5, '', 'all').pipe(
      tap(response => {
        this.recentNotifications.set(response.content);
      })
    );
  }

  /**
   * Refrescar el conteo de notificaciones no leídas
   */
  refreshUnreadCount(): void {
    this.getUnreadCount().subscribe();
  }

  /**
   * Refrescar notificaciones recientes
   * Solo se usa cuando se abre el dropdown de notificaciones
   */
  refreshRecentNotifications(): void {
    this.getRecentNotifications().subscribe();
  }

  // ===========================================================================
  // Tiempo real (SSE) — entrega instantánea de notificaciones nuevas
  // ===========================================================================
  //
  // Se autentica con la cookie de sesión (withCredentials), igual que el resto de
  // peticiones; EventSource no puede enviar headers, pero sí cookies. Es best-effort:
  // la fuente de verdad es la BD, así que ante cualquier fallo se cae a polling del
  // contador y se reintenta la conexión con backoff exponencial.

  private eventSource: EventSource | null = null;
  private fallbackTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempts = 0;
  private visibilityListener: (() => void) | null = null;

  /** Pestaña oculta: el polling de respaldo no pide nada hasta que vuelva a verse. */
  private static isTabHidden(): boolean {
    return typeof document !== 'undefined' && document.hidden;
  }

  /** Abre el stream SSE. Idempotente: si ya hay conexión, no hace nada. */
  connect(): void {
    if (this.eventSource || typeof EventSource === 'undefined') {
      return;
    }
    this.watchVisibility();
    this.openStream();
  }

  /** Cierra el stream y detiene timers. Llamar al destruir el contexto autenticado / logout. */
  disconnect(): void {
    this.reconnectAttempts = 0;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.stopFallbackPolling();
    this.closeEventSource();
    if (this.visibilityListener) {
      document.removeEventListener('visibilitychange', this.visibilityListener);
      this.visibilityListener = null;
    }
  }

  /** Al volver a la pestaña con el polling activo, se resincroniza al momento (sin esperar el tick). */
  private watchVisibility(): void {
    if (this.visibilityListener || typeof document === 'undefined') {
      return;
    }
    this.visibilityListener = () => {
      if (!NotificationService.isTabHidden() && this.fallbackTimer) {
        this.zone.run(() => this.refreshUnreadCount());
      }
    };
    document.addEventListener('visibilitychange', this.visibilityListener);
  }

  private openStream(): void {
    const es = new EventSource(`${this.apiUrl}/stream`, { withCredentials: true });
    this.eventSource = es;

    // Todos los handlers se ejecutan dentro de la zona de Angular (NgZone.run): EventSource
    // no está parcheado por zone.js y, sin esto, los cambios de signal no re-renderizarían la
    // vista hasta el siguiente evento de zona o un refresco de página.
    es.addEventListener('connected', () => this.zone.run(() => {
      this.reconnectAttempts = 0;
      this.stopFallbackPolling();
      // Resincronizar el contador por si llegaron notificaciones mientras estuvo caído.
      this.refreshUnreadCount();
    }));

    es.addEventListener('notification', (event: Event) => this.zone.run(() => {
      try {
        const dto = JSON.parse((event as MessageEvent).data) as NotificationDTO;
        this.onRealtimeNotification(dto);
      } catch {
        // Payload inesperado: al menos refrescar el contador.
        this.refreshUnreadCount();
      }
    }));

    // Evento transitorio de actividad de ticket (no toca el contador ni el panel de campana).
    es.addEventListener('support_ticket_activity', (event: Event) => this.zone.run(() => {
      try {
        const data = JSON.parse((event as MessageEvent).data) as { ticketId: number };
        if (typeof data?.ticketId === 'number') {
          this.lastTicketActivity.set({ ticketId: data.ticketId, seq: ++this.ticketActivitySeq });
        }
      } catch {
        /* payload inesperado: se ignora (el polling de respaldo cubre el hueco) */
      }
    }));

    es.onerror = () => this.zone.run(() => {
      // EventSource reintenta solo en estado CONNECTING; mientras tanto, polling cubre el hueco.
      this.startFallbackPolling();
      // Si la conexión quedó CLOSED (p. ej. sesión expirada), reconectamos con backoff.
      if (es.readyState === EventSource.CLOSED) {
        this.scheduleReconnect();
      }
    });
  }

  private onRealtimeNotification(dto: NotificationDTO): void {
    this.unreadCount.update((c) => c + 1);
    this.recentNotifications.update((list) => {
      if (list.some((n) => n.id === dto.id)) {
        return list;
      }
      return [dto, ...list].slice(0, 5);
    });
    // Notificar a las vistas suscritas (módulo de Notificaciones) la nueva notificación.
    this.lastRealtime.set(dto);
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) {
      return;
    }
    this.reconnectAttempts++;
    // Backoff exponencial con tope de 30s para no martillar al backend.
    const delay = Math.min(30_000, 1_000 * 2 ** this.reconnectAttempts);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.closeEventSource();
      this.openStream();
    }, delay);
  }

  private startFallbackPolling(): void {
    if (this.fallbackTimer) {
      return;
    }
    this.fallbackTimer = setInterval(() => {
      if (!NotificationService.isTabHidden()) {
        this.refreshUnreadCount();
      }
    }, 15_000);
  }

  private stopFallbackPolling(): void {
    if (this.fallbackTimer) {
      clearInterval(this.fallbackTimer);
      this.fallbackTimer = null;
    }
  }

  private closeEventSource(): void {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
  }
}
